// All imports
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, FormControl, FormLabel, Input, Switch, Text } from '@chakra-ui/react';
import {
  Plus,
  DollarSign,
  ArrowUp,
  ArrowRight,
  ArrowDown,
  Ban,
  Lock,
  Unlock,
  Loader2,
  X,
  Edit,
  History,
} from 'lucide-react';
import { getPlayers, createPlayer, updatePlayer, toggleLockPlayer, bulkUpdateAllowedWin } from '../actions/playerActions';
import { getGames } from '../actions/gameActions';
import { getAgents } from '../actions/agentActions';
import { getStokezes } from '../actions/stokezActions';
import { getSuperStokezes } from '../actions/superStokezActions';
import CascadingDropdown from '../components/CascadingDropdown';
import GameSelection from './GameSelection';
import GenericTable from '../components/GenericTable';
import BalanceTransferModal from '../components/BalanceTransferModal';
import PlayerHistoryModal from '../components/PlayerHistoryModal';
import TableSearchBar from '../components/TableSearchBar';
import { throwUnlessApiSuccess } from '../utils/apiResponse';
import { applyRecipientBalancePatch } from '../utils/applyRecipientBalancePatch';
import styles from '../styles/ManagementTable.module.css';
import { usePrivilege } from '../hooks/usePrivilege';
import { useFieldPrivilege } from '../hooks/useFieldPrivilege';
import { useAppToast } from '../hooks/useAppToast';
import { getLoggedInUserRole, isSuperAdminRole } from '../utils/auth';
import { findById, filterGamesByAssignedIds, idStr } from '../utils/entityIds';

const emptyPlayerForm = () => ({
  username: '',
  password: '',
  agent_id: '',
  stokez_id: '',
  super_stokez_id: '',
  admin_id: '',
  company_id: '',
  gameIds: [],
  balance: 0,
  is_active: true,
  commission_perc: 5,
  target_perc: 20,
  adminTargetNTP: '',
  defaultNTP: 20,
  currentNTP: 0,
  auto_claim: true,
  allow_print: true,
  low_chip_enabled: true,
  mac_check: true,
  winBlock: false,
  ntpBlock: false,
  manualBlock: false,
  manual_ntp: 'AUTO',
  allowed_win: '',
  default_allowed_win: '',
  give_manual_win: false,
  high_limit: '',
  negative_win_limit: -1000,
  isLocked: false,
});

const isValidNonNegative = (value) =>
  value !== '' && value != null && !Number.isNaN(Number(value)) && Number(value) >= 0;

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const BoolBadge = ({ value, yesLabel = 'Yes', noLabel = 'No' }) => (
  <span className={`${styles.statusBadge} ${value ? styles.statusActive : styles.statusInactive}`}>
    {value ? yesLabel : noLabel}
  </span>
);

const PlayerManagement = () => {
  const { can } = usePrivilege();
  const { filterColumns, canShowField } = useFieldPrivilege();
  const { showSuccess, showWarning, showApiError } = useAppToast();
  const isSuperAdmin = isSuperAdminRole(getLoggedInUserRole());
  const canSetAllowedWin = can('player.setAllowedWin');
  const canSetNegativeWinLimit = can('player.setNegativeWinLimit');
  const canGiveManualWin = can('player.giveManualWin');
  const canSetHighLimit = can('player.setHighLimit');
  const dispatch = useDispatch();
  const location = useLocation();
  const [bulkAllowedWinModal, setBulkAllowedWinModal] = useState({ isOpen: false, value: '', saving: false });
  const handleOpenTransferModal = (toRole, toId, recipient = {}) => {
    setTransferDetails({
      toRole,
      toId,
      recipientBalance: recipient.balance,
      recipientBalanceCheck: recipient.balance_check,
    });
    setIsTransferOpen(true);
  };

  const handleMacCheckToggle = async (playerId, mac_check) => {
    try {
      await dispatch(updatePlayer(playerId, { command: 'checkPlayermac', mac_check: !mac_check }));
      showSuccess(`Player ${mac_check ? 'Check Mac' : 'Mac check disable'}`);
      dispatch(getPlayers());
    } catch (error) {
      showApiError(error, 'Error toggling MAC check');
    }
  };

  const handleBalanceTransfer = async ({ fromRole, fromId, toRole, toId, amount }) => {
    const response = await axios.post(`${process.env.REACT_APP_API_URL_ADMIN}`, {
      apiName: "user",
      command: 'transferBalance',
      fromRole,
      fromId,
      toRole,
      toId,
      amount
    });
    return throwUnlessApiSuccess(response);
  };

  const { players, loading, error } = useSelector(state => state.player);
  const games = useSelector(state => state.game.games);
  const agents = useSelector(state => state.agent.agents);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filterAgentId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const agentParam = params.get('agent') || null;
    if (!agentParam) return null;
    const found = agents.find((a) => a.username === agentParam);
    return found ? found._id : null;
  }, [location.search, agents]);

  const [searchQuery, setSearchQuery] = useState('');

  const filteredByAgent = useMemo(() => {
    if (!filterAgentId) return players;
    return players.filter((p) => p.agent_id === filterAgentId);
  }, [players, filterAgentId]);

  const filteredPlayers = useMemo(() => {
    if (!searchQuery.trim()) return filteredByAgent;
    const q = searchQuery.trim().toLowerCase();
    return filteredByAgent.filter((p) => {
      const username = (p.username || '').toLowerCase();
      const agent = (p.agent_name || '').toLowerCase();
      const stokez = (p.stokez_name || '').toLowerCase();
      const company = (p.company_name || '').toLowerCase();
      const macId = (p.mac_id || '').toLowerCase();
      const gameCodes = (p.game_codes || []).join(' ').toLowerCase();
      return [username, agent, stokez, company, macId, gameCodes].some((str) => str.includes(q));
    });
  }, [filteredByAgent, searchQuery]);
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [transferDetails, setTransferDetails] = useState({});
  const [targetPercModal, setTargetPercModal] = useState({ isOpen: false, playerId: '', targetPerc: '' });
  const [historyPlayer, setHistoryPlayer] = useState(null);

  const [newPlayer, setNewPlayer] = useState(emptyPlayerForm);
  const [availableGames, setAvailableGames] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [cascadingDropdownValues, setCascadingDropdownValues] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          dispatch(getPlayers()), dispatch(getGames()), dispatch(getAgents()),
          dispatch(getStokezes()), dispatch(getSuperStokezes())
        ]);
      } catch (err) {
        showApiError(err, 'Error fetching data');
      }
    };
    fetchData();
  }, [dispatch, showApiError]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setNewPlayer(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }, []);

  const handleCascadingDropdownChange = useCallback((selections) => {
    setNewPlayer(prev => ({ ...prev, ...selections, gameIds: [] }));
  }, []);

  useEffect(() => {
    if (!newPlayer.agent_id) {
      setAvailableGames([]);
      return;
    }
    const selectedAgent = findById(agents, newPlayer.agent_id);
    const filteredGames = filterGamesByAssignedIds(games, selectedAgent?.gameIds);
    setAvailableGames(
      filteredGames.length
        ? filteredGames
        : games.length && newPlayer.agent_id
          ? games
          : []
    );
  }, [newPlayer.agent_id, agents, games]);

  const handleGameSelection = useCallback((gameId) => {
    const idStrVal = idStr(gameId);
    setNewPlayer(prev => {
      const prevGameIds = (prev.gameIds || []).map(idStr);
      return {
        ...prev,
        gameIds: prevGameIds.includes(idStrVal)
          ? prevGameIds.filter(id => id !== idStrVal)
          : [...prevGameIds, idStrVal],
      };
    });
  }, []);

  const handleApprove = async (playerId) => {
      try {
        await dispatch(updatePlayer(playerId, { command: 'approveLogin' }));
        showSuccess('Player Approved');
        dispatch(getPlayers());
      } catch (error) {
        showApiError(error, 'Error approving player');
      }
    };
  
  const handleToggleLock = async (playerId, isLocked) => {
    try {
      await dispatch(toggleLockPlayer(playerId));
      dispatch(getPlayers());
      showSuccess(`Player ${isLocked ? 'unlocked' : 'locked'}.`);
    } catch (error) {
      showApiError(error, 'Failed to update lock status.');
    }
  };

    const handleReleaseMac = async (playerId) => {
      try {
        await dispatch(updatePlayer(playerId, { command: 'releaseMac' }));
        showSuccess('MAC Released');
        dispatch(getPlayers());
      } catch (error) {
        showApiError(error, 'Error releasing MAC');
      }
    };
  

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      if (!newPlayer.agent_id || !newPlayer.stokez_id || !newPlayer.super_stokez_id || !newPlayer.admin_id || !newPlayer.company_id) {
        showWarning('Please select all required fields');
        return;
      }
      if (canSetAllowedWin && !isValidNonNegative(newPlayer.allowed_win)) {
        showWarning('Allowed Win is required and must be a number ≥ 0');
        return;
      }
      if (canSetAllowedWin && !isValidNonNegative(newPlayer.default_allowed_win)) {
        showWarning('Default Allowed Win is required and must be a number ≥ 0');
        return;
      }
      const highLimit = newPlayer.high_limit === '' || newPlayer.high_limit == null ? 0 : Number(newPlayer.high_limit);
      if (canSetHighLimit && !isValidNonNegative(highLimit)) {
        showWarning('High Limit must be a number ≥ 0');
        return;
      }
      if (isEditing) {
        const allowedWin = Number(newPlayer.allowed_win);
        let negativeWinLimit = Number(newPlayer.negative_win_limit);
        if (newPlayer.negative_win_limit === '' || newPlayer.negative_win_limit == null) {
          negativeWinLimit = -1000;
        }
        if (Number.isNaN(negativeWinLimit) || !Number.isFinite(negativeWinLimit)) {
          showWarning('Negative Win Limit must be a finite number');
          return;
        }
        const updatePayload = {
          username: newPlayer.username,
          agent_id: newPlayer.agent_id,
          stokez_id: newPlayer.stokez_id,
          super_stokez_id: newPlayer.super_stokez_id,
          admin_id: newPlayer.admin_id,
          company_id: newPlayer.company_id,
          gameIds: newPlayer.gameIds || [],
          is_active: newPlayer.is_active,
          commission_perc: Number(newPlayer.commission_perc),
          target_perc: Number(newPlayer.target_perc),
          defaultNTP: Number(newPlayer.defaultNTP),
          currentNTP: Number(newPlayer.currentNTP),
          auto_claim: Boolean(newPlayer.auto_claim),
          allow_print: Boolean(newPlayer.allow_print),
          low_chip_enabled: Boolean(newPlayer.low_chip_enabled),
          mac_check: Boolean(newPlayer.mac_check),
          winBlock: Boolean(newPlayer.winBlock),
          ntpBlock: Boolean(newPlayer.ntpBlock),
          manualBlock: Boolean(newPlayer.manualBlock),
          manual_ntp: newPlayer.manual_ntp || 'AUTO',
          isLocked: Boolean(newPlayer.isLocked),
        };
        if (newPlayer.password) {
          updatePayload.password = newPlayer.password;
        }
        if (canSetAllowedWin) {
          updatePayload.allowed_win = allowedWin;
          updatePayload.default_allowed_win = Number(newPlayer.default_allowed_win);
        }
        if (canSetNegativeWinLimit) {
          updatePayload.negative_win_limit = negativeWinLimit;
        }
        if (canGiveManualWin) {
          updatePayload.give_manual_win = Boolean(newPlayer.give_manual_win);
        }
        if (canSetHighLimit) {
          updatePayload.high_limit = highLimit;
        }
        if (isSuperAdmin) {
          updatePayload.adminTargetNTP =
            newPlayer.adminTargetNTP === '' || newPlayer.adminTargetNTP == null
              ? ''
              : newPlayer.adminTargetNTP;
        }
        if (!isSuperAdmin && (updatePayload.target_perc < 0 || updatePayload.target_perc > 100)) {
          showWarning('Target percentage must be between 0 and 100');
          return;
        }
        await dispatch(updatePlayer(newPlayer._id, updatePayload));
        showSuccess('Player updated');
      } else {
        if (!isSuperAdmin) {
          const targetPerc = Number(newPlayer.target_perc);
          if (!Number.isNaN(targetPerc) && (targetPerc < 0 || targetPerc > 100)) {
            showWarning('Target percentage must be between 0 and 100');
            return;
          }
        }
        let negativeWinLimit = Number(newPlayer.negative_win_limit);
        if (!canSetNegativeWinLimit || newPlayer.negative_win_limit === '' || newPlayer.negative_win_limit == null) {
          negativeWinLimit = -1000;
        }
        if (Number.isNaN(negativeWinLimit) || !Number.isFinite(negativeWinLimit)) {
          showWarning('Negative Win Limit must be a finite number');
          return;
        }
        const createPayload = {
          ...newPlayer,
          allowed_win: canSetAllowedWin ? Number(newPlayer.allowed_win) : 0,
          default_allowed_win: canSetAllowedWin ? Number(newPlayer.default_allowed_win) : 0,
          give_manual_win: canGiveManualWin ? Boolean(newPlayer.give_manual_win) : false,
          high_limit: canSetHighLimit ? highLimit : 0,
          negative_win_limit: negativeWinLimit,
        };
        if (!isSuperAdmin) {
          delete createPayload.adminTargetNTP;
        }
        await dispatch(createPlayer(createPayload));
        showSuccess('Player created');
      }
      setIsModalOpen(false);
      setNewPlayer(emptyPlayerForm());
      setIsEditing(false);
      dispatch(getPlayers());
    } catch (error) {
      showApiError(error, `Failed to ${isEditing ? 'update' : 'create'} Player.`);
    }
  }, [
    dispatch,
    isEditing,
    isSuperAdmin,
    canSetAllowedWin,
    canSetNegativeWinLimit,
    canGiveManualWin,
    canSetHighLimit,
    newPlayer,
    showSuccess,
    showWarning,
    showApiError,
  ]);

  const handleEdit = useCallback((player) => {
    setIsEditing(true);
    setNewPlayer({
      ...emptyPlayerForm(),
      ...player,
      password: '',
      gameIds: (player.gameIds || []).map((id) => String(id)),
      commission_perc: player.commission_perc ?? 5,
      target_perc: player.target_perc ?? 20,
      adminTargetNTP: player.adminTargetNTP ?? '',
      defaultNTP: player.defaultNTP ?? 20,
      currentNTP: player.currentNTP ?? 0,
      auto_claim: player.auto_claim !== false,
      allow_print: player.allow_print !== false,
      low_chip_enabled: player.low_chip_enabled !== false,
      mac_check: player.mac_check !== false,
      winBlock: Boolean(player.winBlock),
      ntpBlock: Boolean(player.ntpBlock),
      manualBlock: Boolean(player.manualBlock),
      manual_ntp: player.manual_ntp || 'AUTO',
      allowed_win: player.allowed_win ?? '',
      default_allowed_win: player.default_allowed_win ?? player.allowed_win ?? '',
      give_manual_win: Boolean(player.give_manual_win),
      high_limit: Number(player.high_limit) > 0 ? player.high_limit : '',
      negative_win_limit: player.negative_win_limit ?? -1000,
      isLocked: Boolean(player.isLocked),
      is_active: player.is_active !== false,
    });
    setCascadingDropdownValues({
      company_id: player.company_id,
      admin_id: player.admin_id,
      super_stokez_id: player.super_stokez_id,
      stokez_id: player.stokez_id,
      agent_id: player.agent_id
    });
    const selectedAgent = findById(agents, player.agent_id);
    const agentGames = filterGamesByAssignedIds(games, selectedAgent?.gameIds);
    const playerGameIdSet = new Set((player.gameIds || []).map(idStr));
    const playerAssignedGames = games.filter((g) => playerGameIdSet.has(idStr(g._id)));
    const mergedGames = [...agentGames];
    playerAssignedGames.forEach((g) => {
      if (!mergedGames.some((m) => idStr(m._id) === idStr(g._id))) {
        mergedGames.push(g);
      }
    });
    setAvailableGames(mergedGames);
    setIsModalOpen(true);
  }, [games, agents]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setNewPlayer(emptyPlayerForm());
    setCascadingDropdownValues({});
    setIsEditing(false);
  }, []);

  const columns = useMemo(() => [

    { Header: 'Username', accessor: 'username', defaultWidth: 120 },
    { Header: 'Password', accessor: 'password', defaultWidth: 100 },
    { Header: 'Agent', accessor: 'agent_name', defaultWidth: 120 },
    { Header: 'Stokez', accessor: 'stokez_name', defaultWidth: 120 },
    { Header: 'Super', accessor: 'super_stokez_name', defaultWidth: 140 },
    { Header: 'Admin', accessor: 'admin_name', defaultWidth: 120 },
    { Header: 'Company', accessor: 'company_name', defaultWidth: 150 },
    { Header: 'Balance', accessor: 'balance', defaultWidth: 100, Cell: ({ row }) => <Text>{row.original.balance}</Text> },
    { Header: 'Game Codes', accessor: 'game_codes', defaultWidth: 150, Cell: ({ value }) => value ? value.join(', ') : '' },
    { Header: 'Comm %', accessor: 'commission_perc', defaultWidth: 80, Cell: ({ value }) => value ?? '—' },
    { Header: 'Target %', accessor: 'target_perc', defaultWidth: 80, Cell: ({ value }) => value ?? '—' },
    { Header: 'Admin Target NTP', accessor: 'adminTargetNTP', defaultWidth: 120, Cell: ({ value }) => (value != null && value !== '' ? value : '—') },
    { Header: 'Default NTP', accessor: 'defaultNTP', defaultWidth: 100, Cell: ({ value }) => value ?? '—' },
    { Header: 'Current NTP', accessor: 'currentNTP', defaultWidth: 100, Cell: ({ value }) => value ?? '—' },
    { Header: 'Manual NTP', accessor: 'manual_ntp', defaultWidth: 100, Cell: ({ value }) => value || '—' },
    { Header: 'Allowed Win', accessor: 'allowed_win', defaultWidth: 110, Cell: ({ value }) => (value != null && value !== '' ? value : '—') },
    {
      Header: 'Default Allowed Win',
      accessor: 'default_allowed_win',
      defaultWidth: 140,
      Cell: ({ value }) => (value != null && value !== '' ? value : '—'),
    },
    {
      Header: 'High Limit',
      accessor: 'high_limit',
      defaultWidth: 110,
      Cell: ({ value }) => (Number(value) > 0 ? value : '—'),
    },
    {
      Header: 'Manual Win',
      accessor: 'give_manual_win',
      defaultWidth: 110,
      Cell: ({ value }) => <BoolBadge value={Boolean(value)} yesLabel="Pending" noLabel="No" />,
    },
    {
      Header: 'Negative Win Limit',
      accessor: 'negative_win_limit',
      defaultWidth: 140,
      Cell: ({ value }) => (value != null && value !== '' ? value : '—'),
    },
    {
      Header: 'Player Status',
      accessor: 'is_active',
      defaultWidth: 120,
      Cell: ({ row }) => {
        const isActive = Boolean(row.original.is_active);
        return (
          <span className={`${styles.statusBadge} ${isActive ? styles.statusActive : styles.statusInactive}`}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        );
      }
    },
    {
      Header: 'Locked',
      accessor: 'isLocked',
      defaultWidth: 90,
      Cell: ({ value }) => <BoolBadge value={Boolean(value)} yesLabel="Locked" noLabel="Open" />,
    },
    {
      Header: 'Auto Claim',
      accessor: 'auto_claim',
      defaultWidth: 100,
      Cell: ({ value }) => <BoolBadge value={value !== false} />,
    },
    {
      Header: 'Allow Print',
      accessor: 'allow_print',
      defaultWidth: 100,
      Cell: ({ value }) => <BoolBadge value={value !== false} />,
    },
    {
      Header: 'Low Chip',
      accessor: 'low_chip_enabled',
      defaultWidth: 90,
      Cell: ({ value }) => <BoolBadge value={value !== false} />,
    },
    {
      Header: 'Win Block',
      accessor: 'winBlock',
      defaultWidth: 90,
      Cell: ({ value }) => <BoolBadge value={Boolean(value)} />,
    },
    {
      Header: 'NTP Block',
      accessor: 'ntpBlock',
      defaultWidth: 90,
      Cell: ({ value }) => <BoolBadge value={Boolean(value)} />,
    },
    {
      Header: 'Manual Block',
      accessor: 'manualBlock',
      defaultWidth: 110,
      Cell: ({ value }) => <BoolBadge value={Boolean(value)} />,
    },
    { Header: 'MAC ID', accessor: 'mac_id', defaultWidth: 120, Cell: ({ value }) => value || '—' },
    { Header: 'Last Login IP', accessor: 'last_login_ip', defaultWidth: 120, Cell: ({ value }) => value || '—' },
    {
      Header: 'Last Login',
      accessor: 'last_login_time',
      defaultWidth: 150,
      Cell: ({ value }) => formatDateTime(value),
    },
    {
      Header: 'Mac check',
      accessor: 'mac_check',
      defaultWidth: 100,
      Cell: ({ row }) => {
        if (!can('player.macReset')) return null;
        const { mac_check, _id } = row.original;
        return (
          <Switch
            isChecked={mac_check}
            onChange={() => handleMacCheckToggle(_id, mac_check)}
            colorScheme={mac_check ? 'green' : 'red'}
          />
        );
      }
    },    
    {
      Header: 'Login Status',
      accessor: 'status',
      defaultWidth: 140,
      Cell: ({ row }) => {
        const { status, is_active: isActive } = row.original;
        if (!isActive) {
          return <Text color="red.500">Inactive</Text>;
        }
        if (status === 'no_login_attempt') {
          return <Text color="gray.500">No Login Attempt</Text>;
        } else if (status === 'waiting_for_approval') {
          if (!can('player.approve')) return <Text color="yellow.600">Waiting</Text>;
          return (
            <Button
              size="sm"
              colorScheme="yellow"
              onClick={() => handleApprove(row.original._id)}
            >
              Approve
            </Button>
          );
        } else if (status === 'approved') {
          if (!can('player.macReset')) return <Text color="green.600">Approved</Text>;
          return (
            <Button
              size="sm"
              colorScheme="blue"
              onClick={() => handleReleaseMac(row.original._id)}
            >
              Release MAC
            </Button>
          );
        }
        return <Text color="red.500">Unknown</Text>;
      }
    },
    {
      Header: 'Created',
      accessor: 'created_at',
      defaultWidth: 150,
      Cell: ({ value }) => formatDateTime(value),
    },
    {
      Header: 'Updated',
      accessor: 'updated_at',
      defaultWidth: 150,
      Cell: ({ value }) => formatDateTime(value),
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      defaultWidth: 300,
      Cell: ({ row }) => {
        const player = row.original;
        return (
          <div className={styles.actionButtons}>
            {can('player.edit') && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => handleEdit(player)}
              title="Edit Player"
              aria-label="Edit Player"
            >
              <Edit size={18} />
            </button>
            )}
            {can('player.transfer') && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => handleOpenTransferModal('player', player.username || player._id, player)}
              title="Transfer Balance"
              aria-label="Transfer Balance"
            >
              <DollarSign size={18} />
            </button>
            )}
            {can('player.setNtp') && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={async () => {
                try {
                  await dispatch(updatePlayer(player._id, { manual_ntp: 'HIGH', manualBlock: true }));
                  dispatch(getPlayers());
                  showSuccess('NTP set to HIGH');
                } catch (error) {
                  showApiError(error, 'Failed to set NTP');
                }
              }}
              title="NTP set to HIGH"
              aria-label="NTP set to HIGH"
            >
              <ArrowUp size={18} />
            </button>
            )}
            {can('player.setTarget') && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => setTargetPercModal({ isOpen: true, playerId: player._id, targetPerc: player.target_perc || '' })}
              title="Set Target %"
              aria-label="Set Target %"
            >
              <ArrowRight size={18} />
            </button>
            )}
            {can('player.setNtp') && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={async () => {
                try {
                  await dispatch(updatePlayer(player._id, { manual_ntp: 'LOWEST', manualBlock: true }));
                  dispatch(getPlayers());
                  showSuccess('NTP set to LOWEST');
                } catch (error) {
                  showApiError(error, 'Failed to set NTP');
                }
              }}
              title="NTP set to LOWEST"
              aria-label="NTP set to LOWEST"
            >
              <ArrowDown size={18} />
            </button>
            )}
            {can('player.lock') && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => handleToggleLock(player._id, player.isLocked)}
              title={player.isLocked ? 'Unlock Player' : 'Lock Player'}
              aria-label={player.isLocked ? 'Unlock Player' : 'Lock Player'}
            >
              {player.isLocked ? <Lock size={18} /> : <Unlock size={18} />}
            </button>
            )}
            {can('player.block') && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={async () => {
                try {
                  await dispatch(updatePlayer(player._id, { is_active: false }));
                  dispatch(getPlayers());
                  showSuccess('Player Deactivated');
                } catch (error) {
                  showApiError(error, 'Failed to deactivate player');
                }
              }}
              title="Deactivate Player"
              aria-label="Deactivate Player"
            >
              <Ban size={18} />
            </button>
            )}
            {can('report.activityLog') && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => setHistoryPlayer({ username: player.username, id: player._id })}
              title="History"
              aria-label="History"
            >
              <History size={18} />
            </button>
            )}
          </div>
        );
      },
    },
  ], [dispatch, handleEdit, can, showSuccess, showApiError, isSuperAdmin]);

  const visibleColumns = useMemo(
    () => filterColumns('player', columns),
    [columns, filterColumns]
  );

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader2 className={styles.spinner} size={48} />
      </div>
    );
  }
  if (error && players.length === 0) {
    return (
      <div className={styles.errorAlert}>
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className={styles.managementPage}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.headerIcon} aria-hidden><Plus size={24} /></span>
          <h1 className={styles.title}>Player Management</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          {isSuperAdmin && (
            <button
              type="button"
              className={styles.addButton}
              onClick={() => setBulkAllowedWinModal({ isOpen: true, value: '', saving: false })}
            >
              <span>Bulk Set Allowed Win</span>
            </button>
          )}
          {can('player.create') && (
          <button
            type="button"
            className={styles.addButton}
            onClick={() => {
              setIsEditing(false);
              setNewPlayer(emptyPlayerForm());
              setIsModalOpen(true);
            }}
          >
            <Plus size={20} />
            <span>Add Player</span>
          </button>
          )}
        </div>
      </div>
      <div className={styles.searchBarRow}>
        <TableSearchBar
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by username, agent, company..."
        />
      </div>
      <div className={styles.tableContainer}>
        <GenericTable
        columns={visibleColumns}
        data={filteredPlayers}
        showColumnFilters={false}
        getRowStyle={(row) => {
          const created = row.created_at != null ? String(row.created_at) : '';
          const updated = row.updated_at != null ? String(row.updated_at) : '';
          if (created && updated && created !== updated) {
            return { backgroundColor: '#feb2b2', color: '#c53030' };
          }
          return {};
        }}
      />
      </div>
      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                {isEditing ? 'Edit Player' : 'Add New Player'}
              </h2>
              <button type="button" className={styles.closeButton} onClick={handleCloseModal} aria-label="Close">
                <X size={20} />
              </button>
            </div>
            <form className={styles.modalForm} onSubmit={handleSubmit}>
              <div className={styles.modalBody}>
                {canShowField('player', 'username', 'form') && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Username <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={newPlayer.username}
                    onChange={handleInputChange}
                    className={styles.input}
                    required
                  />
                </div>
                )}
                {canShowField('player', 'password', 'form') && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Password {isEditing && <span className={styles.helpText}>(Leave blank to keep current password)</span>}
                    {!isEditing && <span className={styles.required}>*</span>}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={newPlayer.password}
                    onChange={handleInputChange}
                    className={styles.input}
                    required={!isEditing}
                  />
                </div>
                )}
                {(canShowField('player', 'agent_id', 'form') ||
                  canShowField('player', 'stokez_id', 'form') ||
                  canShowField('player', 'super_stokez_id', 'form') ||
                  canShowField('player', 'admin_id', 'form') ||
                  canShowField('player', 'company_id', 'form')) && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Agent / Stokez / Super Stokez / Admin / Company <span className={styles.required}>*</span>
                  </label>
                  <CascadingDropdown
                    onSelectionChange={handleCascadingDropdownChange}
                    initialValues={cascadingDropdownValues}
                    roleBeingCreated="player"
                  />
                </div>
                )}
                {canShowField('player', 'gameIds', 'form') && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>Games</label>
                  {availableGames.length > 0 ? (
                    <GameSelection
                      games={availableGames}
                      selectedGames={newPlayer.gameIds || []}
                      onGameSelection={handleGameSelection}
                      isDisabled={!newPlayer.agent_id}
                    />
                  ) : (
                    <p className={styles.helpText}>
                      {newPlayer.agent_id ? 'No games available for this agent' : 'Select an agent to load games'}
                    </p>
                  )}
                </div>
                )}

                {(canShowField('player', 'commission_perc', 'form') ||
                  canShowField('player', 'target_perc', 'form') ||
                  (isSuperAdmin && canShowField('player', 'adminTargetNTP', 'form')) ||
                  canShowField('player', 'defaultNTP', 'form') ||
                  canShowField('player', 'currentNTP', 'form') ||
                  canShowField('player', 'manual_ntp', 'form') ||
                  canShowField('player', 'allowed_win', 'form') ||
                  canShowField('player', 'default_allowed_win', 'form') ||
                  (canSetHighLimit && canShowField('player', 'high_limit', 'form')) ||
                  canShowField('player', 'negative_win_limit', 'form')) && (
                <>
                <h3 className={styles.formSectionTitle}>Percentages &amp; NTP</h3>
                <div className={styles.formGrid}>
                  {canShowField('player', 'commission_perc', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Commission %</label>
                    <input
                      type="number"
                      name="commission_perc"
                      step="any"
                      value={newPlayer.commission_perc}
                      onChange={handleInputChange}
                      className={styles.input}
                    />
                  </div>
                  )}
                  {canShowField('player', 'target_perc', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Target %</label>
                    <input
                      type="number"
                      name="target_perc"
                      step="any"
                      min={isSuperAdmin ? undefined : 0}
                      max={isSuperAdmin ? undefined : 100}
                      value={newPlayer.target_perc}
                      onChange={handleInputChange}
                      className={styles.input}
                    />
                  </div>
                  )}
                  {isSuperAdmin && canShowField('player', 'adminTargetNTP', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Admin Target NTP</label>
                    <input
                      type="number"
                      name="adminTargetNTP"
                      step="any"
                      value={newPlayer.adminTargetNTP}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="Leave empty for none"
                    />
                  </div>
                  )}
                  {canShowField('player', 'defaultNTP', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Default NTP</label>
                    <input
                      type="number"
                      name="defaultNTP"
                      step="any"
                      value={newPlayer.defaultNTP}
                      onChange={handleInputChange}
                      className={styles.input}
                    />
                  </div>
                  )}
                  {canShowField('player', 'currentNTP', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Current NTP</label>
                    <input
                      type="number"
                      name="currentNTP"
                      step="any"
                      value={newPlayer.currentNTP}
                      onChange={handleInputChange}
                      className={styles.input}
                    />
                  </div>
                  )}
                  {canShowField('player', 'manual_ntp', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Manual NTP</label>
                    <select
                      name="manual_ntp"
                      value={newPlayer.manual_ntp || 'AUTO'}
                      onChange={handleInputChange}
                      className={styles.select}
                    >
                      <option value="AUTO">AUTO</option>
                      <option value="HIGH">HIGH</option>
                      <option value="LOW">LOW</option>
                      <option value="LOWEST">LOWEST</option>
                    </select>
                  </div>
                  )}
                  {canSetAllowedWin && canShowField('player', 'allowed_win', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Allowed Win *</label>
                    <input
                      type="number"
                      name="allowed_win"
                      step="any"
                      min="0"
                      required
                      value={newPlayer.allowed_win}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="e.g. 1000"
                    />
                  </div>
                  )}
                  {canSetAllowedWin && canShowField('player', 'default_allowed_win', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Default Allowed Win *</label>
                    <input
                      type="number"
                      name="default_allowed_win"
                      step="any"
                      min="0"
                      required
                      value={newPlayer.default_allowed_win}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="Allowed Win resets to this after a HIGH / manual win"
                    />
                  </div>
                  )}
                  {canSetHighLimit && canShowField('player', 'high_limit', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>High Limit</label>
                    <input
                      type="number"
                      name="high_limit"
                      step="any"
                      min="0"
                      value={newPlayer.high_limit}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="HIGH pays the largest win up to this (empty = normal HIGH)"
                      title="While HIGH, the player gets the largest win up to this amount, even past their own negative-win cap. Other players' limits still apply."
                    />
                  </div>
                  )}
                  {canShowField('player', 'negative_win_limit', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Negative Win Limit</label>
                    <input
                      type="number"
                      name="negative_win_limit"
                      step="any"
                      value={newPlayer.negative_win_limit}
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder="Default -1000"
                      disabled={!canSetNegativeWinLimit}
                      title={canSetNegativeWinLimit ? undefined : 'You do not have permission to change this'}
                    />
                  </div>
                  )}
                </div>
                </>
                )}

                {(canShowField('player', 'auto_claim', 'form') ||
                  canShowField('player', 'allow_print', 'form') ||
                  canShowField('player', 'low_chip_enabled', 'form') ||
                  canShowField('player', 'mac_check', 'form') ||
                  canShowField('player', 'winBlock', 'form') ||
                  canShowField('player', 'ntpBlock', 'form') ||
                  canShowField('player', 'manualBlock', 'form') ||
                  (canGiveManualWin && canShowField('player', 'give_manual_win', 'form')) ||
                  (isEditing && (canShowField('player', 'is_active', 'form') || canShowField('player', 'isLocked', 'form')))) && (
                <>
                <h3 className={styles.formSectionTitle}>Flags</h3>
                <div className={styles.formGrid}>
                  {canShowField('player', 'auto_claim', 'form') && (
                  <label className={styles.switchLabel}>
                    <input
                      type="checkbox"
                      name="auto_claim"
                      checked={Boolean(newPlayer.auto_claim)}
                      onChange={handleInputChange}
                      className={styles.switch}
                    />
                    <span className={styles.switchText}>Auto Claim</span>
                  </label>
                  )}
                  {canShowField('player', 'allow_print', 'form') && (
                  <label className={styles.switchLabel}>
                    <input
                      type="checkbox"
                      name="allow_print"
                      checked={Boolean(newPlayer.allow_print)}
                      onChange={handleInputChange}
                      className={styles.switch}
                    />
                    <span className={styles.switchText}>Allow Print</span>
                  </label>
                  )}
                  {canShowField('player', 'low_chip_enabled', 'form') && (
                  <label className={styles.switchLabel}>
                    <input
                      type="checkbox"
                      name="low_chip_enabled"
                      checked={Boolean(newPlayer.low_chip_enabled)}
                      onChange={handleInputChange}
                      className={styles.switch}
                    />
                    <span className={styles.switchText}>Low Chip Enabled</span>
                  </label>
                  )}
                  {canShowField('player', 'mac_check', 'form') && (
                  <label className={styles.switchLabel}>
                    <input
                      type="checkbox"
                      name="mac_check"
                      checked={Boolean(newPlayer.mac_check)}
                      onChange={handleInputChange}
                      className={styles.switch}
                    />
                    <span className={styles.switchText}>MAC Check</span>
                  </label>
                  )}
                  {canShowField('player', 'winBlock', 'form') && (
                  <label className={styles.switchLabel}>
                    <input
                      type="checkbox"
                      name="winBlock"
                      checked={Boolean(newPlayer.winBlock)}
                      onChange={handleInputChange}
                      className={styles.switch}
                    />
                    <span className={styles.switchText}>Win Block</span>
                  </label>
                  )}
                  {canShowField('player', 'ntpBlock', 'form') && (
                  <label className={styles.switchLabel}>
                    <input
                      type="checkbox"
                      name="ntpBlock"
                      checked={Boolean(newPlayer.ntpBlock)}
                      onChange={handleInputChange}
                      className={styles.switch}
                    />
                    <span className={styles.switchText}>NTP Block</span>
                  </label>
                  )}
                  {canShowField('player', 'manualBlock', 'form') && (
                  <label className={styles.switchLabel}>
                    <input
                      type="checkbox"
                      name="manualBlock"
                      checked={Boolean(newPlayer.manualBlock)}
                      onChange={handleInputChange}
                      className={styles.switch}
                    />
                    <span className={styles.switchText}>Manual Block</span>
                  </label>
                  )}
                  {canGiveManualWin && canShowField('player', 'give_manual_win', 'form') && (
                  <label
                    className={styles.switchLabel}
                    title="Next draw: pay the largest win within Allowed Win, even past this player's own negative-win cap. Cleared after the win."
                  >
                    <input
                      type="checkbox"
                      name="give_manual_win"
                      checked={Boolean(newPlayer.give_manual_win)}
                      onChange={handleInputChange}
                      className={styles.switch}
                    />
                    <span className={styles.switchText}>Give Manual Win</span>
                  </label>
                  )}
                  {isEditing && (
                    <>
                      {canShowField('player', 'is_active', 'form') && (
                      <label className={styles.switchLabel}>
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={Boolean(newPlayer.is_active)}
                          onChange={handleInputChange}
                          className={styles.switch}
                        />
                        <span className={styles.switchText}>Active</span>
                      </label>
                      )}
                      {canShowField('player', 'isLocked', 'form') && (
                      <label className={styles.switchLabel}>
                        <input
                          type="checkbox"
                          name="isLocked"
                          checked={Boolean(newPlayer.isLocked)}
                          onChange={handleInputChange}
                          className={styles.switch}
                        />
                        <span className={styles.switchText}>Locked</span>
                      </label>
                      )}
                    </>
                  )}
                </div>
                </>
                )}

                {isEditing &&
                  (canShowField('player', 'balance', 'form') ||
                    canShowField('player', 'mac_id', 'form') ||
                    canShowField('player', 'last_login_ip', 'form') ||
                    canShowField('player', 'last_login_time', 'form') ||
                    canShowField('player', 'status', 'form')) && (
                  <>
                    <h3 className={styles.formSectionTitle}>Read-only Info</h3>
                    <div className={styles.formGrid}>
                      {canShowField('player', 'balance', 'form') && (
                      <div className={styles.formGroup}>
                        <label className={styles.label}>Balance</label>
                        <input type="text" value={newPlayer.balance ?? 0} readOnly className={styles.input} />
                      </div>
                      )}
                      {canShowField('player', 'mac_id', 'form') && (
                      <div className={styles.formGroup}>
                        <label className={styles.label}>MAC ID</label>
                        <input type="text" value={newPlayer.mac_id || '—'} readOnly className={styles.input} />
                      </div>
                      )}
                      {canShowField('player', 'last_login_ip', 'form') && (
                      <div className={styles.formGroup}>
                        <label className={styles.label}>Last Login IP</label>
                        <input type="text" value={newPlayer.last_login_ip || '—'} readOnly className={styles.input} />
                      </div>
                      )}
                      {canShowField('player', 'last_login_time', 'form') && (
                      <div className={styles.formGroup}>
                        <label className={styles.label}>Last Login Time</label>
                        <input type="text" value={formatDateTime(newPlayer.last_login_time)} readOnly className={styles.input} />
                      </div>
                      )}
                      {canShowField('player', 'status', 'form') && (
                      <div className={styles.formGroup}>
                        <label className={styles.label}>Login Status</label>
                        <input type="text" value={newPlayer.status || '—'} readOnly className={styles.input} />
                      </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className={styles.modalFooter}>
                <button type="submit" className={styles.submitButton}>
                  {isEditing ? 'Update Player' : 'Add Player'}
                </button>
                <button type="button" className={styles.cancelButton} onClick={handleCloseModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <PlayerHistoryModal
        isOpen={Boolean(historyPlayer)}
        onClose={() => setHistoryPlayer(null)}
        username={historyPlayer?.username}
        playerId={historyPlayer?.id}
      />
      <BalanceTransferModal isOpen={isTransferOpen} onClose={() => setIsTransferOpen(false)} toRole={transferDetails.toRole} toId={transferDetails.toId} recipientBalance={transferDetails.recipientBalance} recipientBalanceCheck={transferDetails.recipientBalanceCheck} onTransfer={handleBalanceTransfer} onTransferSuccess={(r) => applyRecipientBalancePatch(dispatch, r)} />
      <Modal isOpen={targetPercModal.isOpen} onClose={() => setTargetPercModal({ isOpen: false, playerId: '', targetPerc: '' })} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Set Target Percentage</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Target Percentage</FormLabel>
              <Input
                type="number"
                min={isSuperAdmin ? undefined : 0}
                max={isSuperAdmin ? undefined : 100}
                step="any"
                value={targetPercModal.targetPerc}
                onChange={(e) => setTargetPercModal(prev => ({ ...prev, targetPerc: e.target.value }))}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={async () => {
              const perc = parseFloat(targetPercModal.targetPerc);
              if (isNaN(perc)) {
                showWarning('Enter a valid percentage');
                return;
              }
              if (!isSuperAdmin && (perc < 0 || perc > 100)) {
                showWarning('Target percentage must be between 0 and 100');
                return;
              }
              try {
                await dispatch(updatePlayer(targetPercModal.playerId, { target_perc: perc, targetNTP: perc, manualBlock: true }));
                dispatch(getPlayers());
                showSuccess('Target % updated');
                setTargetPercModal({ isOpen: false, playerId: '', targetPerc: '' });
              } catch (error) {
                showApiError(error, 'Failed to update target percentage');
              }
            }}>Save</Button>
            <Button variant="ghost" onClick={() => setTargetPercModal({ isOpen: false, playerId: '', targetPerc: '' })}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {isSuperAdmin && (
        <Modal
          isOpen={bulkAllowedWinModal.isOpen}
          onClose={() => {
            if (!bulkAllowedWinModal.saving) {
              setBulkAllowedWinModal({ isOpen: false, value: '', saving: false });
            }
          }}
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Bulk Set Allowed Win</ModalHeader>
            <ModalCloseButton isDisabled={bulkAllowedWinModal.saving} />
            <ModalBody>
              <Text mb={3} fontSize="sm" color="gray.600">
                Updates Allowed Win for players where it is missing, empty, or zero.
              </Text>
              <FormControl isRequired>
                <FormLabel>Allowed Win *</FormLabel>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  placeholder="e.g. 1000"
                  value={bulkAllowedWinModal.value}
                  onChange={(e) => setBulkAllowedWinModal((prev) => ({ ...prev, value: e.target.value }))}
                  isDisabled={bulkAllowedWinModal.saving}
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="blue"
                isLoading={bulkAllowedWinModal.saving}
                onClick={async () => {
                  const value = Number(bulkAllowedWinModal.value);
                  if (
                    bulkAllowedWinModal.value === '' ||
                    bulkAllowedWinModal.value == null ||
                    Number.isNaN(value) ||
                    value < 0
                  ) {
                    showWarning('Allowed Win is required and must be a number ≥ 0');
                    return;
                  }
                  try {
                    setBulkAllowedWinModal((prev) => ({ ...prev, saving: true }));
                    const result = await dispatch(bulkUpdateAllowedWin(value));
                    await dispatch(getPlayers());
                    const modified = result?.modifiedCount ?? 0;
                    showSuccess(
                      modified > 0
                        ? `Allowed Win updated for ${modified} player(s)`
                        : 'No players found with Allowed Win missing or zero'
                    );
                    setBulkAllowedWinModal({ isOpen: false, value: '', saving: false });
                  } catch (error) {
                    setBulkAllowedWinModal((prev) => ({ ...prev, saving: false }));
                    showApiError(error, 'Failed to bulk update Allowed Win');
                  }
                }}
              >
                Update
              </Button>
              <Button
                variant="ghost"
                isDisabled={bulkAllowedWinModal.saving}
                onClick={() => setBulkAllowedWinModal({ isOpen: false, value: '', saving: false })}
              >
                Cancel
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </div>
  );
};

export default PlayerManagement;
